import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, Typography } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { XERO_CONNECT_KEY, XeroOrgChoice, XeroService, xeroErrorText } from '../../services/xero.service';

const BACK = '/integrations?tab=xero';

const storedInstitution = (): string | null => {
    try {
        const raw = window.sessionStorage.getItem(XERO_CONNECT_KEY);
        return raw ? (JSON.parse(raw).institutionId ?? null) : null;
    } catch {
        return null;
    }
};

/** Xero sends the user back here after sign-in. The code and state are single-use, so they are posted once. */
const XeroCallbackPage: React.FC = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();
    const posted = useRef(false);
    const [institutionId] = useState(storedInstitution);
    const [orgs, setOrgs] = useState<XeroOrgChoice[] | null>(null);
    const [picked, setPicked] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const code = params.get('code');
    const state = params.get('state');
    const cancelled = params.get('error');

    useEffect(() => {
        if (posted.current || cancelled) return;
        posted.current = true;
        if (!institutionId || !code || !state) {
            setError('This Xero sign-in link is not valid any more. Start again.');
            return;
        }
        XeroService.callback(institutionId, code, state)
            .then(list => {
                if (list.length === 1) {
                    try { window.sessionStorage.removeItem(XERO_CONNECT_KEY); } catch { /* ignore */ }
                    toast.showSuccess(`Connected to ${list[0].name}`);
                    navigate(BACK, { replace: true });
                } else {
                    setOrgs(list);
                }
            })
            .catch(e => setError(xeroErrorText(e, 'Xero refused the sign-in. Start again.')));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const pick = async () => {
        if (!institutionId || !picked) return;
        setBusy(true);
        try {
            const c = await XeroService.pick(institutionId, picked);
            try { window.sessionStorage.removeItem(XERO_CONNECT_KEY); } catch { /* ignore */ }
            toast.showSuccess(`Connected to ${c.orgName ?? 'Xero'}`);
            navigate(BACK, { replace: true });
        } catch (e) {
            setError(xeroErrorText(e, 'Could not connect that organisation. Start again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 560 }}>
            <Typography variant="h5" gutterBottom>Connect to Xero</Typography>
            {cancelled ? (
                <Alert severity="info" action={<Button component={RouterLink} to={BACK}>Back</Button>}>Xero sign-in was cancelled.</Alert>
            ) : error ? (
                <Alert severity="error" action={<Button component={RouterLink} to={BACK}>Start again</Button>}>{error}</Alert>
            ) : orgs ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography sx={{ mb: 1 }}>You granted access to more than one organisation. Pick the one this company sends to.</Typography>
                    <RadioGroup value={picked} onChange={e => setPicked(e.target.value)} data-help="data.integrations.xero.pickOrg">
                        {orgs.map(o => <FormControlLabel key={o.tenantId} value={o.tenantId} control={<Radio />} label={o.name} />)}
                    </RadioGroup>
                    <Button variant="contained" sx={{ mt: 2 }} disabled={!picked || busy} onClick={pick} data-help="data.integrations.xero.useOrg">
                        Use this organisation
                    </Button>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><CircularProgress size={24} /><Typography>Finishing the Xero sign-in…</Typography></Box>
            )}
        </Box>
    );
};

export default XeroCallbackPage;
